"use client";

import { Children, CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { styles } from "./StatCardCarousel.style";
import { SliderArrow } from "../../elements/SliderArrow";

const FADE_RIGHT = "linear-gradient(to right, black 85%, transparent 100%)";
const FADE_LEFT = "linear-gradient(to left, black 85%, transparent 100%)";
const FADE_BOTH = "linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)";

const SLIDER_MASKS: Record<string, string> = {
  "start-end": "none",
  "start-mobile": FADE_RIGHT,
  "end-mobile": "none",
  "mid-mobile": FADE_RIGHT,
  "start-desktop": FADE_RIGHT,
  "end-desktop": FADE_LEFT,
  "mid-desktop": FADE_BOTH,
};

const getSliderMask = (atStart: boolean, atEnd: boolean, isDesktop: boolean): string => {
  if (atStart && atEnd) return SLIDER_MASKS["start-end"];
  const position = atStart ? "start" : atEnd ? "end" : "mid";
  const viewport = isDesktop ? "desktop" : "mobile";
  return SLIDER_MASKS[`${position}-${viewport}`];
};

export interface StatCardCarouselProps {
  children: ReactNode;
  heading?: string;
  prevAriaLabel: string;
  nextAriaLabel: string;
}

const DESKTOP_SLIDES_TO_SHOW = 3.3;
const MOBILE_SLIDES_TO_SHOW = 1.15;
const DESKTOP_BREAKPOINT = 1024;

export const StatCardCarousel = (props: StatCardCarouselProps) => {
  const { children, heading, prevAriaLabel, nextAriaLabel } = props;

  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState<number | null>(null);
  const slideCount = Children.count(children);

  useEffect(() => {
    const update = () =>
      setSlidesToShow(window.innerWidth >= DESKTOP_BREAKPOINT ? DESKTOP_SLIDES_TO_SHOW : MOBILE_SLIDES_TO_SHOW);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const settings = {
    infinite: false,
    slidesToShow: slidesToShow ?? MOBILE_SLIDES_TO_SHOW,
    slidesToScroll: 1,
    arrows: false,
    swipe: true,
    afterChange: (index: number) => setCurrentSlide(index),
  };

  const resolvedSlidesToShow = slidesToShow ?? MOBILE_SLIDES_TO_SHOW;
  const isAtStart = currentSlide === 0;
  const isAtEnd = currentSlide >= slideCount - resolvedSlidesToShow;
  const isDesktop = resolvedSlidesToShow === DESKTOP_SLIDES_TO_SHOW;
  const sliderMask = getSliderMask(isAtStart, isAtEnd, isDesktop);

  return (
    <div className={styles.wrapper()} style={{ visibility: slidesToShow === null ? "hidden" : "visible" }}>
      {heading && <h2 className={styles.heading()}>{heading}</h2>}
      <div className={styles.sliderWrapper()} style={{ "--slider-mask": sliderMask } as CSSProperties}>
        <Slider ref={sliderRef} key={resolvedSlidesToShow} {...settings}>
          {children}
        </Slider>
      </div>
      {slideCount > DESKTOP_SLIDES_TO_SHOW && (
        <div className={styles.arrowsContainer()}>
          <SliderArrow
            direction="left"
            ariaLabel={prevAriaLabel}
            disabled={isAtStart}
            onClick={() => sliderRef.current?.slickPrev()}
          />
          <SliderArrow
            direction="right"
            ariaLabel={nextAriaLabel}
            disabled={isAtEnd}
            onClick={() => sliderRef.current?.slickNext()}
          />
        </div>
      )}
    </div>
  );
};

StatCardCarousel.displayName = "StatCardCarousel";
